import type { FC } from "react";
import { useForm, useStore } from "@tanstack/react-form";

import { usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import {
  FieldDatepicker,
  FieldInput,
  FieldMultipleCheckbox,
} from "@/components/form";
import DialogCustom from "@/components/modals/dialog-custom";
import HealthModalFooter from "@/components/modals/profil-health/health-modal-footer";
import type { HealthUpdateModalProps } from "@/components/modals/profil-health/health-update-modal";
import { Separator } from "@/components/ui/separator";
import type { PatchWomenHealthFormData } from "@/schemas/patient";
import { patchWomenHealthSchema } from "@/schemas/patient";
import { resetForm } from "@/utils/form";
import { womenHealthOptions } from "@/utils/patient";

const WomenHealthModal: FC<HealthUpdateModalProps> = ({
  healthModalOpen,
  setHealthModalOpen,
  patient,
}) => {
  const { mutate } = usePatchPatient(patient.id);

  const { womenHealth, womenHealthComment, pregnancyEnd } = patient;

  const { Field, handleSubmit, store } = useForm({
    defaultValues: {
      womenHealth,
      womenHealthComment: womenHealthComment ? womenHealthComment : null,
      pregnancyEnd: pregnancyEnd ? pregnancyEnd : null,
    } as PatchWomenHealthFormData,
    onSubmit: async ({ value, formApi }) => {
      const values = patchWomenHealthSchema.parse(value);
      if (values.womenHealth === null) {
        values.womenHealth = [];
      }
      mutate(values, {
        onSuccess: () => {
          setHealthModalOpen("none");
          resetForm(values, formApi);
        },
      });
    },
  });

  const womenHealthValues = useStore(
    store,
    (state) => state.values.womenHealth
  );

  return (
    <DialogCustom
      title="Modifier les informations"
      startIcon={
        <div className="flex p-3 w-12 h-12 justify-center items-center border border-gray-200 rounded-[10px]">
          <Edit className="w-6 h-6" color="black" />
        </div>
      }
      open={healthModalOpen !== "none"}
      onOpenChange={() => setHealthModalOpen("none")}
      className="flex flex-col gap-4"
    >
      <Separator />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full px-6">
        <FieldMultipleCheckbox
          Field={Field}
          schema={patchWomenHealthSchema}
          name="womenHealth"
          options={womenHealthOptions}
          validators={{
            onChange: patchWomenHealthSchema.shape.womenHealth,
          }}
        />
        <div className="flex gap-1">
          {womenHealthValues?.includes("OTHER") ? (
            <FieldInput
              Field={Field}
              schema={patchWomenHealthSchema}
              name="womenHealthComment"
              label="Commentaire"
              placeholder="Ajouter un commentaire"
              validators={{
                onChange: patchWomenHealthSchema.shape.womenHealthComment,
              }}
            />
          ) : null}
          {womenHealthValues?.includes("PREGNANT") ? (
            <FieldDatepicker
              Field={Field}
              schema={patchWomenHealthSchema}
              name="pregnancyEnd"
              label="Date de fin de gestation"
              placeholder="Ajouter un commentaire"
              validators={{
                onChange: patchWomenHealthSchema.shape.pregnancyEnd,
              }}
            />
          ) : null}
        </div>
        <input type="submit" className="hidden" />
      </form>
      <Separator />
      <HealthModalFooter
        setHealthModalOpen={setHealthModalOpen}
        handleSubmit={handleSubmit}
      />
    </DialogCustom>
  );
};

export default WomenHealthModal;
